import { Star } from '@apeiron/star'
import {
  AnimatedBox,
  BlankSVG,
  MayNull,
  SkeletonImage,
  SkeletonTypography
} from '@apeiron/temp'
import { Box, Button, styled, Typography } from '@mui/material'
import ApeironBookmarkButton from '@src/components/share/apeiron/ApeironBookmarkButton'
import ApeironShareButton from '@src/components/share/apeiron/ApeironShareButton'
import StarAstronomicalSlots from '@src/components/stars/detail/StarAstronomicalSlots'
import StarSelectCard from '@src/components/stars/share/StarSelectCard'
import ENV_CONFIG from '@src/config'
import { NftType } from '@src/constants/bookmark'
import { ROUTE } from '@src/constants/route'
import useURL from '@src/hooks/common/router/useURL'
import * as R from 'ramda'
import { FC, ReactElement, useCallback, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

const AddFavoriteButton = styled(ApeironBookmarkButton)`
  margin-left: 5px;
`

const Container = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
`

const NameContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-left: 10px;
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 340px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    justify-content: center;
    width: 100%;
    margin-top: 20px;
  }
`

const ShareButton = styled(ApeironShareButton)`
  margin-left: 5px;
`

const IdText = styled(SkeletonTypography)`
  color: #b1b3b3;
  font-size: 24px;
  font-weight: 300;
  line-height: 18px;
  min-width: 50px;
`

const Name = styled(SkeletonTypography)`
  color: white;
  min-width: 100px;
  font-size: 24px;
  line-height: 18px;
`

const CardContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  ${props => props.theme.breakpoints.down('res1280')} {
    margin-top: 10px;
    width: 100%;
    justify-content: center;
  }
`

const CardButton = styled(Button)`
  padding: unset;
  text-transform: none;
  &:hover {
    background-color: transparent;
  }
`

const StarCard = styled(SkeletonImage)`
  width: 317px;
  height: 437px;
`

const StarCardName = styled(Typography)`
  color: #fee0ae;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  position: absolute;
  text-align: center;
  top: 16px;
  width: 100%;
`

const StarCardNameApeiron = styled(Typography)`
  color: #fee1ae28;
  font-family: var(--font-Apeiron);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  position: absolute;
  text-align: center;
  top: 31px;
  width: 100%;
`

const HoverContainer = styled(AnimatedBox)`
  position: absolute;
  width: 100%;
  height: 100%;
`

const StyledStarAstronomicalSlots = styled(StarAstronomicalSlots)`
  max-width: 100%;
`

const StarDetailCard: FC<Props> = props => {
  const {
    className,
    displaySlots = true,
    isBookmarkable,
    isBookmarked,
    showNameHeader = true,
    showSelectMode = false,
    star,
    hoverComponent,
    onBookmarkChange,
    onClick
  } = props

  const {
    id = '',
    image = BlankSVG,
    tier = { level: 0 },
    name = ''
  } = star || {}

  const tierLevel = Number(R.propOr(0, 'level', tier)) + 1

  const loading = R.isNil(star)

  const TransComponent = (
    <Trans
      i18nKey='star.detail.card.name'
      values={{ tier: `T${tierLevel}`, name }}
    />
  )

  const { buildURL } = useURL()

  const [showHover, setShowHover] = useState(false)

  const handleOnClick = useCallback(() => {
    if (onClick) {
      onClick()
    }
  }, [onClick])

  const handleMouseEnter = useCallback(() => {
    setShowHover(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setShowHover(false)
  }, [])

  const shareURL = useMemo(() => {
    return buildURL(
      ENV_CONFIG.WEB_URL.MARKETPLACE,
      ROUTE.MARKETPLACE_STAR_DETAIL,
      {
        starId: star?.id
      }
    )
  }, [buildURL, star?.id])

  return (
    <Container className={className}>
      <CardContainer>
        <CardButton
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          disabled={R.isNil(onClick)}
          onClick={handleOnClick}
        >
          {showSelectMode ? (
            <StarSelectCard />
          ) : (
            <>
              <StarCard loading={loading} src={image} priority />
              {!loading && (
                <>
                  <StarCardName>{TransComponent}</StarCardName>
                  <StarCardNameApeiron>{TransComponent}</StarCardNameApeiron>
                </>
              )}
              <HoverContainer in={showHover}>{hoverComponent}</HoverContainer>
            </>
          )}
        </CardButton>
        {displaySlots && !showSelectMode && !loading && (
          <StyledStarAstronomicalSlots star={star} />
        )}
      </CardContainer>
      {showNameHeader && (
        <NameContainer>
          <IdText loading={loading}>{`#${id}`}</IdText>
          <Name loading={loading}>{`T${tierLevel} ${name}`}</Name>
          {isBookmarkable && (
            <AddFavoriteButton
              isBookmarked={isBookmarked}
              nftType={NftType.Star}
              tokenId={id}
              onChange={onBookmarkChange}
            />
          )}
          <ShareButton url={shareURL} />
        </NameContainer>
      )}
    </Container>
  )
}

type Props = {
  className?: string
  displaySlots?: boolean
  isBookmarkable: boolean
  isBookmarked: boolean
  showSelectMode?: boolean
  showNameHeader?: boolean
  hoverComponent?: ReactElement
  star: MayNull<Star>
  onBookmarkChange?: (bookmarked: boolean) => void
  onClick?: () => void
}

export default StarDetailCard
